// AddendumActions — a trade job's add-on on the sheet's done step and on the cards (Oct 29, 2026).
//
// Greg: "we could have it detect the agreement is already signed, so a sign and schedule button are below. sign
// brings up addendum, work and price, homeowner signs and is sent a link to open and sign. once signed, the schedule
// button is available." On a signed roof job a homeowner-billed trade waits on the homeowner's add-on
// (trade_item_prices.addendum_status, migration 20261029g): Sign texts them the link (build-calendar
// trade_addendum_send, in the words Greg approved) and says where it went or why it didn't; Schedule opens the Move
// flow once they have signed. Until then the job has no day and isn't on the deal's money.

import { useEffect, useState } from "react";
import { etDate } from "../../lib/weeklySpiffs";
import {
  addendumNotSentText, fmtTradeDay, isAddendumOpen, sendTradeAddendum, type AddendumStatus,
} from "../../lib/trades";

interface Props {
  /** Any visit of the job: the server finds the job's price row. */
  tradeId: string;
  status: AddendumStatus | null | undefined;
  sentAt?: string | null;
  signedAt?: string | null;
  canAct: boolean;
  /** The job's visits with no day yet, each a Schedule button (labelled when there are two). */
  schedule?: Array<{ id: string; label: string }>;
  onSchedule?: (tradeId: string) => void;
  /** After a send went out (the parent reloads its rows). */
  onSent?: () => void;
  /** "done": the sheet's done step shows Schedule before the signature too, turned off; "card": once signed. */
  variant: "done" | "card";
}

const dayOf = (iso: string | null | undefined) => (iso ? fmtTradeDay(etDate(new Date(iso))) : "");

export default function AddendumActions({ tradeId, status, sentAt, signedAt, canAct, schedule = [], onSchedule, onSent, variant }: Props) {
  const [st, setSt] = useState<AddendumStatus | null | undefined>(status);
  const [sent, setSent] = useState<string | null | undefined>(sentAt);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => { setSt(status); setSent(sentAt); }, [status, sentAt]);

  if (!st || st === "not_needed") return null;
  const open = isAddendumOpen(st);
  const signed = st === "signed";

  async function sign() {
    setBusy(true);
    setNote(null);
    try {
      const r = await sendTradeAddendum(tradeId);
      if (r.status === "sent") {
        setSt("sent");
        setSent(r.sent_at ?? new Date().toISOString());
        setNote({ ok: true, text: `Sent to the homeowner's phone ending ${r.phone_last4 ?? "…"}.` });
        onSent?.();
      } else {
        setNote({ ok: false, text: addendumNotSentText(r) });
      }
    } catch (e) {
      setNote({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  const showSchedule = !!onSchedule && schedule.length > 0 && (variant === "done" || signed);
  return (
    <div className={`rounded-lg px-2.5 py-2 text-[12px] ${signed ? "bg-emerald-50 text-emerald-900" : "bg-amber-50 text-amber-900"}`}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-semibold">
          {st === "needs_signature" ? "Add-on: not sent" : st === "sent" ? `Add-on sent ${dayOf(sent)}` : `Add-on signed ${dayOf(signedAt)}`}
        </span>
        {open && <span className="text-[11px] opacity-80">The homeowner signs it by text link; then it gets its day and goes on the invoice.</span>}
      </div>
      {(canAct && (open || showSchedule)) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {open && (
            <button type="button" disabled={busy} onClick={sign}
                    className="px-2.5 py-1 rounded-md bg-bardin-orange text-white text-[12px] font-semibold disabled:opacity-50">
              {busy ? "Sending…" : st === "sent" ? "Sign again" : "Sign"}
            </button>
          )}
          {showSchedule && schedule.map((v) => (
            <button key={v.id} type="button" disabled={!signed} onClick={() => onSchedule!(v.id)}
                    title={signed ? undefined : "Waits for the homeowner's signature"}
                    className="px-2.5 py-1 rounded-md bg-blue-600 text-white text-[12px] font-semibold disabled:bg-slate-300 disabled:text-slate-600">
              {schedule.length > 1 ? `Schedule ${v.label}` : "Schedule"}
            </button>
          ))}
          {showSchedule && !signed && <span className="text-[11px] opacity-80">Waits for the homeowner's signature</span>}
        </div>
      )}
      {note && <div className={`mt-1 text-[11.5px] ${note.ok ? "text-emerald-800" : "text-rose-800"}`}>{note.text}</div>}
    </div>
  );
}
